import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import { editOrganization } from "~/services/server";
import { INTENTS, loadCookiesData } from "~/utils";
import * as APITypes from "~/API";
import { EnrichedJoinRequest } from "~/types/organization";
import { message } from "antd";
import i18n from "~/i18n";
import { apiClient, OrganizationDetailsLoaderResult } from "~/services/apiClient";

export async function OrganizationDetailsPageLoader({
  request,
  params,
}: LoaderFunctionArgs): Promise<OrganizationDetailsPageLoaderType | Response> {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");

  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) return redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;

  const isSuperAdmin = role === APITypes.ManagerRolesType.SuperAdmin;
  const canEditOrganization = isSuperAdmin;
  const canDeleteOrganization = isSuperAdmin;

  const organizationID = params.organizationID || "";

  const result = await apiClient.loader<OrganizationDetailsLoaderResult>(
    "organization-details",
    { id: organizationID, userID: user.uid }
  );

  if (!result.success || !result.data) {
    console.error("Failed to load organization details:", result.error);
    throw new Error(
      result.error ?? "Failed to load organization details"
    );
  }

  const data = result.data;

  return {
    organizationDetails: data.organizationDetails,
    membersList: data.membersList,
    joinRequests: data.joinRequests as EnrichedJoinRequest[],
    ownerID,
    ownerType,
    userName: data.userName,
    canEditOrganization,
    canDeleteOrganization,
  };
}

export type OrganizationDetailsPageLoaderType = {
  organizationDetails: APITypes.Organization;
  membersList: APITypes.User[];
  joinRequests: EnrichedJoinRequest[];
  ownerID: string;
  ownerType: APITypes.OwnerType;
  userName: string;
  canEditOrganization: boolean;
  canDeleteOrganization: boolean;
};

export const OrganizationDetailsPageAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const organizationID = params.organizationID || "";

  switch (intent) {
    case INTENTS._edit_organization: {
      const payload = JSON.parse(formData.get("body") as string);
      const result = await editOrganization({
        id: organizationID,
        ...payload,
      });
      return result;
    }
    case INTENTS._delete_organization: {
      // To be implemented
      console.log("Delete organization: To be implemented");
      return null;
    }
    case INTENTS._remove_member: {
      // To be implemented
      console.log("Remove member: To be implemented");
      return null;
    }
    case INTENTS._approve_join_request: {
      const requestID = (formData.get("requestID") as string)?.trim();
      if (!requestID) {
        message.error("Request ID is required");
        return null;
      }
      const result = await apiClient.sendIntent<{ approved: boolean }>(
        INTENTS._approve_join_request,
        { id: requestID }
      );
      if (result.success) {
        message.success(i18n.t("Request_Approved_Success"));
      } else {
        message.error(result.error ?? i18n.t("Sorry_something_went_wrong"));
      }
      return result;
    }
    case INTENTS._deny_join_request: {
      const raw = formData.get("body") as string;
      const payload = raw ? (JSON.parse(raw) as { id?: string; requestID?: string; _version?: number }) : {};
      const id = payload.id ?? payload.requestID;
      if (!id) {
        message.error("Request ID is required");
        return null;
      }
      const result = await apiClient.sendIntent<{ denied: boolean }>(
        INTENTS._deny_join_request,
        { id, _version: payload._version }
      );
      if (result.success) {
        message.success(i18n.t("Request_Denied_Success"));
      } else {
        message.error(result.error ?? i18n.t("Sorry_something_went_wrong"));
      }
      return result;
    }
    default:
      return null;
  }
};
